import React, { Component } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Checkbox, FormControlLabel, TextField, Typography, Link, Stack, Card as MuiCard } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Divider } from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { withRouter } from "./withRouter";
import { takeStoryEntry } from "./utils/storyEntry";

const GlassCard = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: { maxWidth: 460 },

  /* glassmorphism */
  background: 'rgba(255,255,255,0.75)',
  backdropFilter: 'blur(11px)',
  border: '1px solid rgba(255,255,255,0.35)',
  borderRadius: 20,
  boxShadow:
    '0 8px 32px 0 rgba(31, 38, 135, 0.15), 0 6px 20px 0 rgba(0,0,0,0.08)',

  /* dark‑mode tweak */
  ...theme.applyStyles('dark', {
    background: 'rgba(30,30,40,0.35)',
    border: '1px solid rgba(255,255,255,0.12)',
  }),
}));


axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.withCredentials = true;

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  marginTop: '10vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

class SignIn extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentUser: false,
      registrationToggle: false,
      email: '',
      username: '',
      password: '',
      registrationSuccess: false,
      errorDialogOpen: false,
      errorDialogMessage: '',
      plan: 'trial',
      confirmDialogOpen: false,
      loginError: '',
      registrationError: '',
      verifyMessage: '',
      verifySeverity: 'success',
      registrationSuccessMessage: '',
      loadingVerify: false,
    };
  }

  // Where an authenticated user goes next. Normally the themed landing page — but
  // someone who followed a link to one highlights story and ended up here (a gated
  // story turned them away, or a phone deep link) is returned to that story
  // instead. The marker is only ever set by that journey, so an ordinary sign-in
  // is unaffected. See utils/storyEntry.
  goAfterAuth = (res) => {
    const story = takeStoryEntry();
    if (story) return this.props.navigate(story, { replace: true });
    return this.props.navigate(res?.data?.is_demo_user ? '/demo-pol' : '/pol');
  };

  componentDidMount() {
    axios.get('/api/user-info/')
      .then((res) => {
        this.setState({ currentUser: true });
        this.goAfterAuth(res);
      })
      .catch(() => {
        this.setState({ currentUser: false });
      });

    const params = new URLSearchParams(window.location.search);
    // ?signup=1 (e.g. from the highlights reels' closing CTA) lands directly on
    // the "Create Demo Access" form instead of the sign-in form.
    if (params.get('signup') === '1') this.setState({ registrationToggle: true });
    if (params.get('demoVerify') !== '1') return;
    const uid = params.get('uid');
    const token = params.get('token');
    if (!uid || !token) return;

    this.setState({ loadingVerify: true });
    axios.post('/api/user/demo-verify/', { uid, token })
      .then(() => {
        this.setState({ verifyMessage: 'Email verified. You are now signed in.', verifySeverity: 'success', loadingVerify: false });
        this.props.navigate('/demo-pol');
      })
      .catch((error) => {
        this.setState({
          verifyMessage: error?.response?.data?.error || 'Verification link is invalid or expired.',
          verifySeverity: 'error',
          loadingVerify: false,
        });
      });
  }
  
  

  handleOpenErrorDialog = (message) => {
    this.setState({ errorDialogOpen: true, errorDialogMessage: message });
  };

  handleCloseErrorDialog = () => {
    this.setState({ errorDialogOpen: false, errorDialogMessage: '' });
  };

  updateFormBtn = () => {
    this.setState((prevState) => ({ registrationToggle: !prevState.registrationToggle }));
  };
  
//   submitLogin = (e) => {
//     e.preventDefault();
//     const { email, password } = this.state;
//     const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     // Email format check
//     if (!EMAIL_REGEX.test(email)) {
//       return this.setState({ loginError: 'Please enter a valid email address.' });
//     }
//     // Password required
//     if (!password) {
//       return this.setState({ loginError: 'Please enter your password.' });
//     }
  
//     // Clear any client-side error and call API
//     this.setState({ loginError: '' }, () => {
//       axios
//         .post('/api/user/login/', { email, password })
//         .then(() => this.props.history.push('/pol'))
//         .catch(() =>
//           this.setState({ loginError: 'Invalid email or password.' })
//         );
//     });
//   };
submitLogin = (e) => {
    e.preventDefault();
    const { email, password } = this.state;
  
    axios
    .post('/api/user/login/', { email, password })
    .then(() => axios.get('/api/user-info/'))
    .then((res) => this.goAfterAuth(res))
    .catch((error) =>
      this.setState({ loginError: error?.response?.data?.error || 'Invalid email or password.' })
    );
}
  
  // 1) All fields required
  submitRegistration = async (e) => {
    e.preventDefault();
    const { email, username, password } = this.state;
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

    // full name 2 words
    if (username.trim().split(' ').length < 2) {
      return this.setState({ registrationError: 'Please enter your full name.' });
    }
    // require all fields
    if (!username || !email || !password) {
      return this.setState({ registrationError: 'All fields are required.' });
    }
  
    // email format
    if (!EMAIL_REGEX.test(email)) {
      return this.setState({ registrationError: 'Enter a valid email address.' });
    }
  
    // password can have symbols but must have letters and a number
    if (!PASSWORD_REGEX.test(password)) {
      return this.setState({
        registrationError:
          'Password must be at least 8 characters long and include at least one letter and one number.',
      });
    }
  
   
    this.setState({ registrationError: '' }, this.handleConfirmedRegistration);
  };
  
  
  

  handleConfirmedRegistration = async () => {
    const { email, username, password } = this.state;
  
    try {
      const response = await axios.post('/api/user/demo-signup/', {
        email,
        name: username,
        password,
      });
      const verifyLink = response?.data?.verify_url;
  
      this.setState({
        confirmDialogOpen: false,
        registrationSuccess: true,
        registrationError: '',
        registrationSuccessMessage: verifyLink
          ? `Demo account created. Dev verify link: ${verifyLink}`
          : 'Demo signup created. Check your email to verify and activate access.',
      });
    } catch (error) {
      const detail =
        error?.response?.data?.error ||
         JSON.stringify(error?.response?.data) ||
          'Registration failed.';
    this.setState({ registrationError: detail });
    }
    
  };
  
  

  render() {
    const {
      registrationToggle,
      email,
      username,
      password,
      verifyMessage,
      verifySeverity,
      registrationSuccessMessage,
      loadingVerify,
    } = this.state;
    

    const backgroundImageStyle = {
      backgroundImage: `url('/static/images/districtstatic.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '0px',
    };

    return (
      <SignInContainer direction="column" style={backgroundImageStyle}>
        <GlassCard
          sx={{
            p: { xs: 3, sm: 5 },
            width: { xs: '91%', sm: 420, md: 480 },
            gap: 1.5,
          }}
        >
       
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <img src="/static/images/districtprofiles2.png" alt="Logo" width={220} />
          </Box>
    
          <Typography
            variant="h4"
            fontWeight={700}
            align="center"
            sx={{ letterSpacing: 0.5 }}
            
          >
            {registrationToggle ? 'Create Demo Access' : 'Member Sign-In'}
          </Typography>

          {verifyMessage && (
            <Alert severity={loadingVerify ? 'info' : verifySeverity}>{verifyMessage}</Alert>
          )}

          {/* {registrationToggle && (
            <Typography
              variant="subtitle2"
              align="center"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              No payment details required
            </Typography>
          )} */}
              
      
          <Box
            component="form"
            onSubmit={registrationToggle ? this.submitRegistration : this.submitLogin}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            {this.state.registrationSuccess && registrationToggle && (
              <Alert severity="success">
                {registrationSuccessMessage || 'Demo account created. Check your email to verify and activate access.'}
              </Alert>
            )}
    
            {registrationToggle ? (
              <>
               <TextField
                  label="Full Name"
                  fullWidth
                  value={username}
                  onChange={(e) =>
                this.setState({ username: e.target.value, registrationError: '' })
                  }
                />
                <TextField
                  type="email"
                  label="Email Address"
                  fullWidth
                  value={email}
                  onChange={(e) =>
                this.setState({ email: e.target.value, registrationError: '', registrationSuccess: false })
                  }
                />
                <TextField
                  type="password"
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(e) =>
                this.setState({ password: e.target.value, registrationError: '' })
                  }
                />
            {registrationToggle && this.state.registrationError && (
              <Box
              sx={{
                mt: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.main',
              }}
            >
              <WarningAmber sx={{ mr: 1 }} />
              <Typography variant="body2">
                {this.state.registrationError}
              </Typography>
            </Box>
            )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={this.state.registrationSuccess || loadingVerify}
                  sx={{
                    py: 1.3,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    opacity: this.state.registrationSuccess ? 0.6 : 1,
                    pointerEvents: this.state.registrationSuccess ? 'none' : 'auto'
                  }}
                >
                  {this.state.registrationSuccess ? 'Created' : 'Create Demo Account'}
                </Button>
    
                <Typography variant="body2" align="center">
                  Need full access instead?{' '}
                  <Link
                    href="mailto:strata@stackdatastrategy.com"
                    underline="none"
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Contact Us
                  </Link>
                </Typography>
              </>
            ) : (
              <>
                              <TextField 
                  type="email"
                  label="Email Address"
                  fullWidth
                  value={email}
                  onChange={(e) =>
                this.setState({ email: e.target.value, loginError: '' })
                  }
                />
                <TextField
                  type="password"
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(e) =>
                this.setState({ password: e.target.value, loginError: '' })
                  }
                />
                {this.state.loginError && (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mt: 1, textAlign: 'center' }}
                  >
                    {this.state.loginError}
                  </Typography>
                )}
                <FormControlLabel
                  control={<Checkbox color="primary" />}
                  label="Remember me"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loadingVerify}
                  sx={{
                    py: 1.3,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                  }}
                >
                  Sign In
                </Button>
              </>
            )}

          </Box>
    
          
          <Divider sx={{ my: .5 }} />
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    alignItems="center"
    justifyContent="center"
    spacing={1.5}
    mb={.5}
  >
    <Typography
      variant="subtitle"                    
      color="text.secondary"
      sx={{ fontWeight: 400 }}
    >
      {registrationToggle ? 'Already have an account?' : 'New here?'}
    </Typography>

    <Button
      size="medium" 
      onClick={this.updateFormBtn}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        letterSpacing: 0.5,
        color: 'primary.main',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.2)',
        },
      }}
      variant="outlined"
    >
      {registrationToggle ? 'Sign In' : 'Sign up for a demo'}
    </Button>
  </Stack>

        </GlassCard>
      </SignInContainer>
    );
    
  }
}

export default withRouter(SignIn);
